<script setup>
// 미디어 허브 섹션 — 반응형으로 옮긴 다섯 번째 섹션.
//
// 원본: Figma 홈(ver1) 4133:20422 (1920x925), 배경 #151512
//   왼쪽 글 x 215 y 150 w 584
//     눈썹   15px semibold, 테두리 #ffe3af, 알약      (아래 간격 20)
//     제목   56px bold 행간 66, 2줄                    (아래 간격 30)
//     소제목 22px 행간 30                              (아래 간격 10)
//     본문   20px 행간 28, 4줄                         (아래 간격 40)
//     버튼   180x42 "미디어허브 바로가기"
//     로고   y 720, 248x55 (글 칸 아래 112)
//   오른쪽 썸네일 모자이크 x 960~1960, y 150~775, 280x195 아홉 장
//     (오른쪽 끝이 1920 을 넘어 잘려 나가는 것이 원본 구도다)
//
// 원본에서 바꾼 것
//   1) 왼쪽 글만 빼내 반응형으로 다시 짰다. 글자는 전부 원본 값 고정이고
//      제목만 clamp 로 감쌌다(1280 이상 원본 56px 고정).
//   2) 썸네일 모자이크와 배경 장식은 글이 아니라 그림이라 1920 캔버스 층에 그대로 둔다.
//      배율은 창 폭 / 1920 이고 상한은 1 이다 — 1920 을 넘으면 더 커지지 않는다.
//      영상 9개가 들어 있어 좌표를 건드리면 위험해서 손대지 않았다.
//   3) 1024 아래에서는 글이 모자이크 위에 겹치지 않게 한 칸으로 내려간다.
//   4) 글 칸 높이를 모자이크 블록과 같은 625(y150~775)로 묶고 유튜브 로고를 바닥에
//      붙였다(2026-09-26 요청). 원본은 로고 아랫변(720+55)과 썸네일 셋째 줄
//      아랫변(580+195)이 둘 다 775 로 같은데, 왼쪽을 흐름으로 다시 짜면서
//      눈썹 높이 +3 · 눈썹->제목 간격 +10 · 버튼 높이 +3 이 쌓여 로고가 15 내려갔다.
//      mt 를 깎아 맞추면 글이 한 줄만 달라져도 또 어긋나므로 바닥에 붙인다.
//
// 배경은 제 것을 갖지 않는다(bare) — 섹션 뒤에 깔린 디졸브 배경이 색을 맡는다.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  SCENE_TEXT_STAGGER_MS,
  SCENE_TEXT_SUB_MS,
  MEDIA_SLIDE_MS,
  MEDIA_THUMBS_SLIDE_MS,
  MEDIA_THUMBS_SLIDE_PX,
  MEDIA_VIDEO_IDS,
  SCENE_REVEAL_DELAY_MS,
  sceneTextReveal,
} from '@/data/homeMotionTiming.js'
import imgRectangle240649515 from '@/assets/figma/3e676817fa010f9bdaf5f81db5065c1db6a571fa.png'
import imgImage219 from '@/assets/figma/f763b24c4a0c99fcd59705971c5e8bd83a47eadb.png'
import imgRectangle240649521 from '@/assets/figma/6d2e3e329bc8c351920f35905fa2ff9dbeae14d8.png'
import imgRectangle240649518 from '@/assets/figma/55e30a7df93902b561d3b9c2ca9b51183d18e6a5.png'
import imgRectangle240649517 from '@/assets/figma/b7abf16161c98845e5858b9ffca2aee3f6301bee.png'
import imgRectangle240649522 from '@/assets/figma/fad49f63e07cdb2142248212421711afa5300618.png'
import imgImage34 from '@/assets/figma/78ce6959d56388ad5e289427ae3c7ee07f8e736a.png'
import imgRectangle240649519 from '@/assets/figma/937d05ae4062d9f4b786bd767ba67f233be344d7.png'
import imgImage205 from '@/assets/figma/2f359bd563968b2508391edb22b0f3227dbb459b.png'
import imgRectangle240649516 from '@/assets/figma/b26505789b8b6f1db72154069d6ea30763cab8bd.png'
import imgImage14 from '@/assets/figma/b237e4b548ed1c95ff3d9192584bc80bd658aefb.png'
import imgRectangle240649523 from '@/assets/figma/bab724b993bd0646b26898d5d8df630813abc83b.png'
import imgChzzklogoKorBlack1 from '@/assets/figma/22c8d41da0e8aa44122f4b44dc33cc198e2301a9.png'
import imgRectangle240649520 from '@/assets/figma/c18781a720d862cf026c35125d0428957afe3950.png'
import imgImage233 from '@/assets/figma/c3cce4dcd79473d4bcead92c48b5243c9ae9c469.png'
import imgGroup from '@/assets/figma/0d4a9a4cd6f3b0bfe9a1d3e92731cdc51cfc8731.svg'
import imgGroup1 from '@/assets/figma/43eba1f882b80f733bc74977ee34ce03e2e62f3c.svg'
import imgGroup2 from '@/assets/figma/8151f2a8a8a9ce699f35bfb0ebf0c91a5e71d4aa.svg'
import imgGroup2117905546 from '@/assets/figma/ce758decb7c2faf9d9463ba477e511651d0b1f29.svg'
import MediaVideo from '@/components/home/MediaVideo.vue'
import { loadYouTubeIframeApi } from '@/composables/useYouTubeIframeApi.js'
const style0 = { containerType: "size" }

const props = defineProps({
  // 현재 스토리보드 프레임 번호 (홈1-N)
  step: { type: Number, default: 36 },
  // 배경을 끄고 나온다.
  bare: { type: Boolean, default: false },
})

const FIRST_FRAME = 33
const revealed = computed(() => props.step >= FIRST_FRAME)

// 원본 연출 그대로. 글은 순서대로 올라오고, 모자이크는 오른쪽에서 밀려 들어온다.
// 글은 모든 섹션이 같은 효과를 쓴다.
const curtain = (order) => sceneTextReveal(revealed.value, order)

// 그림(유튜브 로고)은 옆에서 미끄러져 들어오는 제 연출을 그대로 둔다.
const appear = (order, { rise = 0, slideX = 0 } = {}) => ({
  opacity: revealed.value ? 1 : 0,
  transform: revealed.value ? 'none' : `translate(${slideX}px, ${rise}px)`,
  transitionProperty: 'opacity, transform',
  transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
  transitionDuration: `${MEDIA_SLIDE_MS}ms`,
  transitionDelay: revealed.value
    ? `${SCENE_REVEAL_DELAY_MS + order * SCENE_TEXT_STAGGER_MS}ms`
    : '0ms',
})

const thumbsStyle = computed(() => ({
  opacity: revealed.value ? 1 : 0,
  transform: revealed.value ? 'none' : `translateX(${MEDIA_THUMBS_SLIDE_PX}px)`,
  transitionProperty: 'opacity, transform',
  transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
  transitionDuration: `${MEDIA_THUMBS_SLIDE_MS}ms`,
  transitionDelay: revealed.value ? `${SCENE_REVEAL_DELAY_MS}ms` : '0ms',
}))
// 유튜브 로고도 서브 텍스트와 같이 나온다. 예전에는 여섯 번째 차례라 1150ms 였는데,
// 글이 두 단계로 줄면서 혼자 720ms 뒤처져 보였다.
const youtubeStyle = computed(() => appear(1))

// 영상은 섹션이 들어오기 전에 미리 데워 두고, 들어오면 보여 준다.
const warm = ref(false)
const shown = ref(false)
let warmTimer = null
watch(revealed, (on) => {
  if (!on) return
  warm.value = true
  // 글이 두 단계로 줄면서 기다릴 시간도 줄었다(옛 값은 여섯 단계를 셌다).
  warmTimer = setTimeout(
    () => (shown.value = true),
    SCENE_TEXT_SUB_MS + MEDIA_THUMBS_SLIDE_MS
  )
}, { immediate: true })
const videoFor = (i) => MEDIA_VIDEO_IDS[i % MEDIA_VIDEO_IDS.length]

// ── 그림 층 배율 ─────────────────────────────────────────────────────
// 모자이크와 배경 장식은 1920 좌표 그대로 두고 통째로 줄인다.
// 상한 1 — 1920 을 넘는 화면에서는 더 키우지 않는다(캔버스 섹션과 같은 규칙).
const DESIGN_W = 1920
const DESIGN_H = 925
// 모자이크 캔버스에서 실제로 칠해지는 자리는 y117~775 다. 위 117 은 원본이
// 프레임 안에 둔 여백인데, 배경을 뺀 뒤로는 그냥 빈 자리로 보인다(2026-09-26).
// 캔버스를 그만큼 올려 섹션 맨 위가 곧 첫 썸네일이 되게 한다.
const ART_TRIM = 117

const root = ref(null)
const artScale = ref(1)
let observer = null

const measureArt = () => {
  const w = root.value?.clientWidth
  if (!w) return
  // 배율은 화면 뿌리(HomeSceneView)의 zoom 이 전담한다. 여기서 또 줄이면 두 번 줄어든다.
  artScale.value = 1
}

onMounted(() => {
  measureArt()
  observer = new ResizeObserver(measureArt)
  observer.observe(root.value)
  loadYouTubeIframeApi()
})
onBeforeUnmount(() => {
  clearTimeout(warmTimer)
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <section
    ref="root"
    class="relative flex w-full flex-col justify-center overflow-hidden"
    :class="{ 'bg-[#151512]': !bare }"
    :style="{ '--r': artScale }"
    data-node-id="4133:20422"
  >
    <!-- 회색 유튜브 워터마크는 여기 없다. 배경이라 SceneBackdrop 으로 옮겼다
         (2026-09-26: 배경과 콘텐츠를 분리). 섹션 안에 두면 콘텐츠 높이에 잘리고,
         스크롤할 때 그 잘린 직선이 화면을 가로질러 지나간다. -->

    <!-- 그림 층. 썸네일 모자이크는 1920 좌표 그대로다. -->
    <div
      aria-hidden="false"
      class="absolute left-1/2 origin-top -translate-x-1/2"
      :style="{
        top: `${-ART_TRIM}px`,
        width: `${DESIGN_W}px`,
        height: `${DESIGN_H}px`,
        transform: `translateX(-50%) scale(${artScale})`,
      }"
    >
        <div class="absolute inset-0" :style="thumbsStyle">
          <div class="overflow-hidden absolute h-[195px] left-[1380px] rounded-[30px] top-[150px] w-[280px]" data-node-id="4133:20469">
            <MediaVideo :video-id="videoFor(1)" :active="warm" :show="shown" :order="1">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649515" />
            </MediaVideo>
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1395px] px-[12px] py-[8px] rounded-[8px] top-[296px]" data-node-id="4133:20470">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20471">
              골드나라 엘시티 금소녀
            </p>
          </div>
          <div class="absolute bottom-[740px] h-[20px] right-[275px] w-[73px]" data-node-id="4133:20472" data-name="image 219">
            <div class="absolute inset-0 overflow-hidden pointer-events-none">
              <img alt="" class="absolute h-[170%] left-0 max-w-none top-[-22.22%] w-full" :src="imgImage219" />
            </div>
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[1380px] rounded-[30px] top-[580px] w-[280px]" data-node-id="4133:20473">
            <MediaVideo :video-id="videoFor(7)" :active="warm" :show="shown" :order="7">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649521" />
            </MediaVideo>
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1395px] px-[12px] py-[8px] rounded-[8px] top-[726px]" data-node-id="4133:20474">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20475">
              배재한의 아우라
            </p>
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[1260px] rounded-[30px] top-[365px] w-[280px]" data-node-id="4133:20476">
            <MediaVideo :video-id="videoFor(4)" :active="warm" :show="shown" :order="4">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649518" />
            </MediaVideo>
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1275px] px-[12px] py-[8px] rounded-[8px] top-[511px]" data-node-id="4133:20477">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20478">
              배재한 창원귀금속협회 회장
            </p>
          </div>
          <div class="absolute contents left-[1080px] top-[150px]" data-node-id="4133:20479">
            <div class="overflow-hidden absolute h-[195px] left-[1080px] rounded-[30px] top-[150px] w-[280px]" data-node-id="4133:20480">
              <div class="absolute inset-0 overflow-hidden pointer-events-none rounded-[30px]">
                <MediaVideo :video-id="videoFor(0)" :active="warm" :show="shown" :order="0">
                  <img alt="" class="absolute h-[122.97%] left-0 max-w-none top-[-16.74%] w-full" :src="imgRectangle240649517" />
                </MediaVideo>
              </div>
            </div>
            <div class="absolute bottom-[740px] h-[20px] right-[575px] w-[73px]" data-node-id="4133:20481" data-name="image 219">
              <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <img alt="" class="absolute h-[170%] left-0 max-w-none top-[-22.22%] w-full" :src="imgImage219" />
              </div>
            </div>
            <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1095px] px-[12px] py-[8px] rounded-[8px] top-[296px]" data-node-id="4133:20482">
              <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20483">
                배재한의 금토크
              </p>
            </div>
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[1080px] rounded-[30px] top-[580px] w-[280px]" data-node-id="4133:20484">
            <MediaVideo :video-id="videoFor(6)" :active="warm" :show="shown" :order="6">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649522" />
            </MediaVideo>
          </div>
          <div class="-translate-x-1/2 absolute bottom-[315px] h-[15px] left-[calc(50%+359px)] w-[52px]" data-node-id="4133:20485" data-name="image 34">
            <div class="absolute inset-0 overflow-hidden pointer-events-none">
              <img alt="" class="absolute h-[141.94%] left-[-40.24%] max-w-none top-[-29.03%] w-[140.3%]" :src="imgImage34" />
            </div>
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1095px] px-[12px] py-[8px] rounded-[8px] top-[726px]" data-node-id="4133:20486">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20487">
              goldnara_aura
            </p>
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[960px] rounded-[30px] top-[365px] w-[280px]" data-node-id="4133:20488">
            <MediaVideo :video-id="videoFor(3)" :active="warm" :show="shown" :order="3">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649519" />
            </MediaVideo>
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[975px] px-[12px] py-[8px] rounded-[8px] top-[511px]" data-node-id="4133:20489">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20490">
              절대 실패 없는 금 투자
            </p>
          </div>
          <div class="absolute bottom-[513px] h-[32px] right-[695px] w-[56px]" data-node-id="4133:20491" data-name="image 205">
            <div class="absolute inset-0 overflow-hidden pointer-events-none">
              <img alt="" class="absolute h-[202.41%] left-[-10.24%] max-w-none top-[-41.83%] w-[116.86%]" :src="imgImage205" />
            </div>
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[1680px] rounded-[30px] top-[150px] w-[280px]" data-node-id="4133:20492">
            <MediaVideo :video-id="videoFor(2)" :active="warm" :show="shown" :order="2">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649516" />
            </MediaVideo>
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1695px] px-[12px] py-[8px] rounded-[8px] top-[296px]" data-node-id="4133:20493">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20494">
              아우라 금거래소
            </p>
          </div>
          <div class="absolute bottom-[742px] h-[18px] right-[-25px] w-[51px]" data-node-id="4133:20495" data-name="image 14">
            <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgImage14" />
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[1680px] rounded-[30px] top-[580px] w-[280px]" data-node-id="4133:20496">
            <MediaVideo :video-id="videoFor(8)" :active="warm" :show="shown" :order="8">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649523" />
            </MediaVideo>
          </div>
          <div class="-translate-x-1/2 -translate-y-1/2 absolute h-[17px] left-[calc(50%+958.5px)] top-[calc(50%+141px)] w-[55px]" data-node-id="4133:20497" data-name="chzzklogo_kor(Black) 1">
            <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgChzzklogoKorBlack1" />
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1695px] px-[12px] py-[8px] rounded-[8px] top-[726px]" data-node-id="4133:20498">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20499">
              배재한의 금토크
            </p>
          </div>
          <div class="overflow-hidden absolute h-[195px] left-[1560px] rounded-[30px] top-[365px] w-[280px]" data-node-id="4133:20500">
            <MediaVideo :video-id="videoFor(5)" :active="warm" :show="shown" :order="5">
              <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[30px] size-full" :src="imgRectangle240649520" />
            </MediaVideo>
          </div>
          <div class="absolute left-[1794px] size-[31px] top-[380px]" data-node-id="4133:20501" data-name="image 233">
            <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgImage233" />
          </div>
          <div class="absolute backdrop-blur-[10px] bg-[rgba(255,255,255,0.05)] content-stretch flex items-center justify-center left-[1575px] px-[12px] py-[8px] rounded-[8px] top-[511px]" data-node-id="4133:20502">
            <p class="[word-break:break-word] font-medium leading-[18px] not-italic relative shrink-0 text-[13px] text-white whitespace-nowrap" data-node-id="4133:20503">
              골드나라 네이버 카페
            </p>
          </div>
        </div>
    </div>

    <!-- 왼쪽 글. 반응형이고 글자는 원본 값 그대로다. -->
    <!-- 위아래 여백은 없다(2026-09-26: 콘텐츠 안의 여백을 전부 걷었다).
         한때 pt150/pb117 이 있었는데, 배경을 SceneBackdrop 으로 뺀 뒤로는 그 자리가
         그냥 빈 여백으로 보여 앞뒤 경계의 간격이 리듬(120)보다 117 씩 벌어졌다.
         모자이크 캔버스도 같이 117 올렸다(ART_TRIM) - 글 칸과 썸네일의 정렬은 그대로다. -->
    <!-- 상자 폭을 줄임(--fit)으로 나눈다. 이 섹션만 줄어드는 해상도가 있는데,
         줄면 좌우 여백까지 같이 줄어 글이 안쪽으로 밀린다 - 1351x610 에서 왼쪽
         시작이 144(거래소개·앱)가 아니라 252 였다(2026-09-26 제보).
         나눠 두면 줄어든 만큼 상자가 넓어져 화면에서의 자리가 그대로다.
         제목 크기를 --fit 으로 되돌린 것과 같은 방식이다. -->
    <div
      class="relative mx-auto flex w-full"
      :style="{
        maxWidth: 'calc(1530px / var(--fit, 1))',
        paddingLeft: 'calc(20px / var(--fit, 1))',
        paddingRight: 'calc(20px / var(--fit, 1))',
      }"
    >
      <!-- 높이를 모자이크에서 칠해지는 세로 길이(775 - 117 = 658)에 묶는다. 로고가 그 바닥,
           곧 썸네일 셋째 줄 아랫변에 붙는다.
           min-h 라 글이 넘치면 칸이 늘고 로고는 글을 따라간다 — 겹치지 않는다. -->
      <!-- 폭은 원본이 584 였다. 그런데 제목 첫 줄("Real Money Trade를")이 581 이라
           3px 차이로 컴퓨터마다 석 줄로 접혔다. 접히면 그 한 줄(74)이 로고 위
           mt-auto 여백을 먹어 로고가 버튼에 붙는다(2026-09-26 제보).
           640 으로 넓혀 여유를 준다 — 글 칸 오른끝 1033, 모자이크 왼끝 1703 이라
           겹칠 일이 없다. -->
      <div class="flex w-full max-w-[640px] flex-col items-start lg:min-h-[658px]">
        <span class="media-eyebrow" :style="curtain(0)">Media Herb</span>

        <h2 class="media-title mt-[30px]" :style="curtain(0)">
          Real Money Trade를<br />위한 미디어 허브
        </h2>

        <p class="media-lead mt-[30px]" :style="curtain(1)">금,은 거래 미디어 정보</p>

        <div class="mt-[10px]" :style="curtain(1)">
          <p class="media-body">실물자산 시장을 움직이는 핵심 데이터를 분석하고,</p>
          <p class="media-body">경제 이슈와 글로벌 시장 변화 속에서 금과 은의 가치를 조명합니다.</p>
          <p class="media-body">시장 전망부터 투자 전략, 업계 트렌드까지</p>
          <p class="media-body">REAL GOLD만의 깊이 있는 인사이트를 만나보세요.</p>
        </div>

        <span class="media-cta mt-[40px]" :style="curtain(1)">
          미디어허브 바로가기
          <span class="media-arrow"><img :src="imgGroup2117905546" alt="" /></span>
        </span>

        <div class="relative mt-[112px] h-[55px] w-[248px] shrink-0 overflow-clip lg:mt-auto" :style="youtubeStyle" data-node-id="4133:20428" data-name="_?덉씠??1">
          <div class="absolute inset-[0_68.11%_0_0]" data-node-id="4133:20429" data-name="Group">
            <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgGroup1" />
          </div>
          <div class="absolute inset-[3.93%_0.01%_6.17%_35.04%]" data-node-id="4133:20433" data-name="Group">
            <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgGroup2" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.media-eyebrow {
  display: inline-flex;
  align-items: center;
  /* 붉은색으로(2026-09-27 요청). #ff0132 는 이 섹션의 유튜브 로고가 쓰는 빨강이다
     - 새 색을 들이지 않고 섹션 안에 이미 있는 값을 쓴다. 다른 섹션 뱃지는
     여전히 금색이다(#ffe3af 어두운 바탕, #a66e08 밝은 바탕). */
  border: 1px solid #ff0132;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #ff0132;
  white-space: nowrap;
}

/* 제목만 창 폭을 탄다. 1280 을 넘으면 원본 56px 에 걸려 더 커지지 않는다.
   (56 / 1280 * 100 = 4.375vw) */
.media-title {
  /* 여섯 섹션 제목을 50 으로 맞췄다(2026-09-26 요청). 원본은 56 이었다.
     어느 해상도에서도 같은 크기라야 해서, 섹션이 통째로 줄어들 때(--fit)
     제목만 그만큼 되돌려 상쇄한다. */
  font-size: calc(50px / var(--fit, 1));
  line-height: 1.18;
  font-weight: 700;
  color: #fff;
  word-break: keep-all;
}

.media-lead {
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  color: #fff;
  word-break: keep-all;
}

.media-body {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  color: #fff;
  word-break: keep-all;
}

.media-cta {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 10px 14px;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  color: #fff;
  white-space: nowrap;
}
.media-arrow {
  display: block;
  width: 16px;
  height: 16px;
  transform: rotate(90deg);
}
.media-arrow img {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
